import { useIsAgentHomeV1Enabled } from '@/features/flags';
import { useResetProjectsComposerContext } from '@/features/projects/resetProjectsComposerContext';
import { getChatHref, WORK_CHAT_HOME_PATH } from '@heidi/agents/contracts';
import {
  pendingWorkHomeViewAtom,
  pendingWorkModeAtom,
  useBaseChatHref,
} from '@heidi/agents/data';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { routeChatIdFromQuery } from './routeChatIdFromQuery';

const WORK_CHAT_ROUTE_PATTERN = '/scribe/chat/projects/[[...id]]';

// undefined = no pending; null = pending navigation to /scribe/chat (no ID); string = pending specific chat
type PendingChatId = string | null | undefined;

interface ChatSidebarHandlers {
  /** Current chat ID extracted from the URL if on chat page, null otherwise */
  currentChatId: string | null;
  /**
   * Navigate to a specific chat or to the base chat page if no ID provided.
   * Pass ``version`` when known so Work harness chats open at ``/projects/[id]``.
   */
  handleSelectChat: (chatId: string, version?: 1 | 2) => void;
  /** Navigate to the base chat page to start a new chat */
  handleNewChat: () => void;
  /** Navigate to the base chat page if the deleted chat was the currently open one */
  handleDeleteChat: (deletedChatId: string) => void;
}

/**
 * Custom hook that provides chat sidebar navigation handlers.
 * Used by both Sidebar (index.tsx) and OtherSidebarPanels to avoid code duplication.
 */
export function useChatSidebarHandlers(): ChatSidebarHandlers {
  const router = useRouter();
  const [pendingChatId, setPendingChatId] = useState<PendingChatId>(undefined);
  // Flag-aware base home shared via ``useBaseChatHref`` so New chat, the
  // empty-selectChat fallback, and the delete-fallback all land on the same
  // home as the "Work"/Evidence nav item.
  const baseChatHref = useBaseChatHref();
  const isAgentHomeV1Enabled = useIsAgentHomeV1Enabled();

  // Projects composer context (the @-linked session/patient chip + the
  // canvas artifact it opens) lives in app-scoped jotai atoms that
  // persist across navigation. A new chat is a fresh thread, so it must
  // not inherit the previous chat's linked context — clear both before
  // landing on the Work home. No-op when Projects is off (those atoms
  // aren't used on the legacy Evidence home).
  const resetProjectsComposerContext = useResetProjectsComposerContext();

  // Reset the Work home to its hero on New chat. A same-route push to
  // /scribe/chat/projects doesn't remount ProjectsPage, so its local view state
  // would otherwise stay on the current sub-view (Workflows/Discover).
  const setPendingWorkHomeView = useSetAtom(pendingWorkHomeViewAtom);
  // Same reasoning for Auto/Work/Evidence mode: reset to Auto so New chat
  // after Evidence mode doesn't silently stay in Evidence.
  const setPendingWorkMode = useSetAtom(pendingWorkModeAtom);

  useEffect(() => {
    const clear = () => setPendingChatId(undefined);
    router.events.on('routeChangeComplete', clear);
    router.events.on('routeChangeError', clear);
    return () => {
      router.events.off('routeChangeComplete', clear);
      router.events.off('routeChangeError', clear);
    };
  }, [router.events]);

  // Get current chat ID from URL if on chat page.
  const routerChatId = useMemo(() => {
    if (!router.pathname.includes('/scribe/chat')) return null;
    return routeChatIdFromQuery(router.query.id);
  }, [router.pathname, router.query.id]);

  // Pending selection takes precedence; falls back to router-derived value after navigation
  const currentChatId = pendingChatId !== undefined ? pendingChatId : routerChatId;

  // Handler to navigate to a specific chat or base chat page
  const handleSelectChat = useCallback(
    (chatId: string, version?: 1 | 2) => {
      setPendingChatId(chatId || null);
      if (chatId) {
        void router.push(getChatHref({ id: chatId, version }), undefined, {
          shallow: false,
        });
      } else {
        void router.push(baseChatHref, undefined, { shallow: false });
      }
    },
    [router, baseChatHref]
  );

  // Agent Home New chat is the Home landing (suggested work stays visible),
  // not a created project. The mounted Work route owns discard and remount.
  // From any other route, reset context and push Home so the page can focus
  // the composer. Legacy Chat keeps the established reset + base-route path.
  const handleNewChat = useCallback(() => {
    if (isAgentHomeV1Enabled && baseChatHref === WORK_CHAT_HOME_PATH) {
      setPendingWorkHomeView('new-chat');
      setPendingWorkMode('work');
      if (router.pathname !== WORK_CHAT_ROUTE_PATTERN) {
        resetProjectsComposerContext();
        setPendingChatId(null);
        void router.push(WORK_CHAT_HOME_PATH, undefined, { shallow: false });
      }
      return;
    }

    resetProjectsComposerContext();
    setPendingWorkHomeView('home');
    setPendingWorkMode('work');
    setPendingChatId(null);
    void router.push(baseChatHref, undefined, { shallow: false });
  }, [
    router,
    baseChatHref,
    isAgentHomeV1Enabled,
    resetProjectsComposerContext,
    setPendingWorkHomeView,
    setPendingWorkMode,
  ]);

  // Handler to navigate back to base chat page if the deleted chat was currently open
  const handleDeleteChat = useCallback(
    (deletedChatId: string) => {
      if (currentChatId === deletedChatId) {
        void router.push(baseChatHref, undefined, { shallow: false });
      }
    },
    [currentChatId, router, baseChatHref]
  );

  return {
    currentChatId,
    handleSelectChat,
    handleNewChat,
    handleDeleteChat,
  };
}
